import { useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { useEvent } from "shared/lib/useEvent"
import { NO_PROJECT_COLOR, Project } from "shared/types/Project"
import { SelectOption } from "shared/types/Select"

import { ActiveProjectSelectModel } from "../ActiveProjectSelect"

export type ActiveProjectSelectViewModel = ReturnType<
  typeof useActiveProjectSelectViewModel
>

export function useActiveProjectSelectViewModel({
  activeProject,
  activeProjectHref,
  isLoading,
  options,
  value,
  tabIndex,
  onNavigateToActiveProject,
  onChange,
  onCreate
}: ActiveProjectSelectModel) {
  const { t } = useTranslation()

  const splitButtonRef = useRef<HTMLDivElement>(null)

  const [inputValue, setInputValue] = useState("")

  const items = useMemo(() => {
    if (!inputValue) return options
    const exists = options.some(
      o => o.label.toLowerCase() === inputValue.toLowerCase()
    )
    if (exists) return options
    return [
      ...options,
      { value: inputValue, label: inputValue, creatable: true, data: undefined }
    ]
  }, [options, inputValue])

  const selectedOption = useMemo(
    () => options.find(o => o.value === value) ?? null,
    [options, value]
  )

  const handleChange = useEvent(
    (option: SelectOption<string, Project> | null) => {
      if (!option) return
      if (option.creatable) {
        onCreate({ id: "new", title: option.label })
      } else {
        onChange(option.value)
      }
    }
  )

  const handleNavigate = useEvent(() => {
    onNavigateToActiveProject()
  })

  const title = activeProject?.title || t("Project.NoActiveProject")
  const color = activeProject?.ui?.color || NO_PROJECT_COLOR

  return {
    activeProject,
    activeProjectHref,
    color,
    isLoading,
    items,
    selectedOption,
    splitButtonRef,
    tabIndex,
    title,
    onInputValueChange: setInputValue,
    onChange: handleChange,
    onNavigateToProject: handleNavigate
  }
}
